import { useMemo } from "react";
import { classNames } from "primereact/utils";
import styles from "./DesignStudioConfirmationPreview.module.scss";
import { IDesignStudioColors } from "../models/designStudioPresetModel";
import { buildDesignStudioPreviewStyle } from "../utils/designStudioPreviewUtility";

interface IDesignStudioConfirmationPreviewProps {
  colors: IDesignStudioColors;
  studioName?: string;
  className?: string;
}

// The same booking the e-mail preview shows, on the page the client lands on after it.
const DETAILS = [
  { icon: "✂️", label: "Услуга", value: "Масаж, 60 мин" },
  { icon: "👤", label: "Специалист", value: "Мария Петрова" },
  { icon: "📅", label: "Дата и час", value: "сряда, 21 октомври в 10:00" },
  { icon: "🕐", label: "Продължителност", value: "1 ч." },
];

const CheckGlyph = () => (
  <svg viewBox="0 0 24 24" className={styles.check_glyph} aria-hidden>
    <path
      d="M5 12.5 10 17.5 19 7"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CalendarGlyph = () => (
  <svg viewBox="0 0 16 16" className={styles.cta_glyph} aria-hidden>
    <rect x="2" y="3.5" width="12" height="10.5" rx="2" fill="none" stroke="currentColor" strokeWidth="1.5" />
    <path d="M2 7h12M5.5 2v3M10.5 2v3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

/**
 * The portal's "reservation confirmed" page, painted with a palette: the headings and the
 * price wear the accent as text, the check and the calendar button wear it as a fill. Every
 * colour is a custom property on the preview's own root, so it never touches the page around it.
 */
export const DesignStudioConfirmationPreview = ({
  colors,
  studioName,
  className,
}: IDesignStudioConfirmationPreviewProps) => {
  const style = useMemo(() => buildDesignStudioPreviewStyle(colors), [colors]);
  const name = studioName?.trim() || "Вашето студио";

  return (
    <div
      className={classNames(styles.root, className)}
      style={style}
      role="img"
      aria-label="Преглед на страницата за потвърждение"
      data-testid="design-studio-confirmation-preview"
    >
      <div className={styles.page}>
        <div className={styles.header}>
          <span className={styles.header_name}>{name}</span>
          <span className={styles.header_address}>бул. „Витоша“ 12, София</span>
        </div>

        <div className={styles.hero}>
          <span className={styles.check}>
            <CheckGlyph />
          </span>
          <span className={styles.title}>
            <span className={styles.title_text}>Ще ви очакваме!</span> 🎉
          </span>
          <span className={styles.subtitle}>
            Вашата резервация е потвърдена! Ще получите имейл за потвърждение.
          </span>
        </div>

        <div className={styles.card}>
          <div className={styles.card_header}>
            <span className={styles.card_title}>Детайли за резервацията</span>
          </div>

          <div className={styles.tiles}>
            <span className={styles.tile}>
              <span className={styles.tile_icon}>🏢</span>
              <span className={styles.tile_text}>
                <span className={styles.tile_label}>Обект</span>
                <span className={styles.tile_value}>{name}</span>
              </span>
            </span>
            {DETAILS.map((detail) => (
              <span key={detail.label} className={styles.tile}>
                <span className={styles.tile_icon}>{detail.icon}</span>
                <span className={styles.tile_text}>
                  <span className={styles.tile_label}>{detail.label}</span>
                  <span className={styles.tile_value}>{detail.value}</span>
                </span>
              </span>
            ))}
            <span className={classNames(styles.tile, styles.tile_price)}>
              <span className={styles.tile_icon}>💰</span>
              <span className={styles.tile_text}>
                <span className={styles.tile_label}>Обща сума</span>
                <span className={styles.tile_value}>€ 45.00</span>
              </span>
            </span>
          </div>
        </div>

        <span className={styles.cta}>
          <CalendarGlyph />
          Добави в календара
        </span>
      </div>
    </div>
  );
};
